import styled from 'styled-components'
import { Card } from 'react-bootstrap'

export const ViewContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: transparent;
`

export const ScrollableContent = styled.div`
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
  min-height: 0;

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;
  scrollbar-width: none;
`

export const PageHeader = styled.div`
  margin-bottom: 1.5rem;
  text-align: center;
  padding: 1.5rem 0;
  background: linear-gradient(180deg, rgba(128, 0, 133, 0.1) 0%, rgba(128, 0, 133, 0) 100%);
  border-radius: 12px;
  border: 1px solid rgba(128, 0, 133, 0.2);

  h1 {
    font-size: 2.5rem;
    font-weight: 600;
    margin-bottom: 0.75rem;
    background: linear-gradient(90deg, #fff 0%, #800085 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  p {
    font-size: 1.1rem;
    color: #b0b0b0;
    max-width: 800px;
    margin: 0 auto;
    line-height: 1.6;
  }
`

export const ToolCard = styled(Card)`
  background: transparent;
  border: 1px solid #333;
  border-radius: 12px;
  transition: all 0.3s ease;
  cursor: pointer;
  height: 100%;

  &:hover {
    border-color: #800085;
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(128, 0, 133, 0.3);
  }

  .card-body {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .card-title {
    color: #fff;
    font-size: 1.2rem;
    font-weight: 600;
    margin-bottom: 0.75rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .card-text {
    color: #b0b0b0;
    font-size: 0.95rem;
    line-height: 1.5;
    flex: 1;
  }

  .tool-icon {
    color: #800085;
    width: 24px;
    height: 24px;
  }
`

export const ToolGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`

export const SectionTitle = styled.h5`
  color: #fff;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .section-icon {
    color: #800085;
  }
`

export const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0.75rem 0 0 0;

  li {
    color: #b0b0b0;
    font-size: 0.9rem;
    margin-bottom: 0.5rem;
    padding-left: 1rem;
    position: relative;

    &:before {
      content: '•';
      color: #800085;
      position: absolute;
      left: 0;
    }
  }
`

export const StatusBadge = styled.span<{ status: 'available' | 'coming-soon' | 'beta' }>`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  margin-left: auto;

  ${(props) => {
    switch (props.status) {
      case 'available':
        return `
          background: rgba(40, 167, 69, 0.2);
          color: #28a745;
          border: 1px solid rgba(40, 167, 69, 0.3);
        `
      case 'beta':
        return `
          background: rgba(255, 193, 7, 0.2);
          color: #ffc107;
          border: 1px solid rgba(255, 193, 7, 0.3);
        `
      case 'coming-soon':
        return `
          background: rgba(108, 117, 125, 0.2);
          color: #6c757d;
          border: 1px solid rgba(108, 117, 125, 0.3);
        `
    }
  }}
`

// Object Selection Components
export const SelectionContainer = styled.div`
  background: transparent;
  border-radius: 12px;
  padding: 2rem;
  margin-bottom: 3rem;
  display: flex;
  flex-direction: column;
`

export const ObjectCard = styled(Card)<{ $selected: boolean }>`
  background: transparent;
  border: 2px solid ${(props) => (props.$selected ? '#800085' : '#333')};
  border-radius: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
  height: 100%;

  &:hover {
    border-color: #800085;
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(128, 0, 133, 0.2);
  }

  .card-body {
    padding: 1rem;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .card-title {
    color: #fff;
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .card-text {
    color: #b0b0b0;
    font-size: 0.9rem;
    flex: 1;
    margin-bottom: 1rem;
  }

  .selection-badge {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    background: #800085;
    color: white;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.8rem;
  }
`

export const ObjectImage = styled.div`
  width: 100%;
  height: 120px;
  background: transparent;
  border-radius: 6px;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #333;
  position: relative;

  .placeholder-icon {
    color: #555;
    width: 48px;
    height: 48px;
  }
`

export const GroupCard = styled(Card)<{ $type: 'catalog' | 'my-group' }>`
  background: ${(props) => (props.$type === 'catalog' ? 'transparent' : 'rgba(128, 0, 133, 0.1)')};
  border: 1px solid ${(props) => (props.$type === 'catalog' ? '#333' : 'rgba(128, 0, 133, 0.3)')};
  border-radius: 8px;
  transition: all 0.3s ease;
  height: 100%;

  &:hover {
    border-color: #800085;
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(128, 0, 133, 0.2);
  }

  .card-body {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    height: 100%;
  }

  .group-header {
    display: flex;
    justify-content: space-between;
    align-items: start;
    margin-bottom: 1rem;
  }

  .group-title {
    color: #fff;
    font-size: 1.1rem;
    font-weight: 600;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .group-description {
    color: #b0b0b0;
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }

  .group-actions {
    display: flex;
    gap: 0.5rem;
  }
`

export const ObjectChip = styled.span`
  background: #333;
  color: #b0b0b0;
  border-radius: 12px;
  padding: 0.25rem 0.5rem;
  margin: 0.25rem;
  font-size: 0.8rem;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;

  .object-icon {
    width: 12px;
    height: 12px;
    color: #800085;
  }

  .remove-btn {
    color: #ff6b6b;
    cursor: pointer;
    margin-left: 0.25rem;

    &:hover {
      color: #ff5252;
    }
  }
`
